/* eslint-disable @typescript-eslint/no-unused-vars */
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { isAfter, subMinutes } from 'date-fns'

import store from 'src/store'

import { actions as moduleActions } from './actions'
import * as moduleState from './state'
import moduleSagas from './sagas'
import * as moduleSelectors from './selectors'
import { CallState, ICall, IToWhomCall, ToolConfiguration } from './types'
import { builderSagaCommunications } from 'src/helpers/redux'
import { IResponse } from 'src/services/accounts/http'

export const slice = createSlice({
  name: moduleState.PREFIX,
  initialState: moduleState.initialState,
  reducers: {
    setWidgetTool(state, action: PayloadAction<ToolConfiguration>) {
      state.widgetOptions.tool = action.payload
    },

    setTwillioState(state, action: PayloadAction<boolean>) {
      state.enabled = action.payload
    },
    setCallState(state, action: PayloadAction<CallState>) {
      state.widgetOptions.callState = action.payload
    },
    setMoreHistoryCall(state, action: PayloadAction<IResponse<ICall[]>>) {
      state.data.callHistory.data = [
        ...(state.data.callHistory?.data ?? []),
        ...action.payload.data,
      ]
      state.data.callHistory.cursor = action.payload.meta.nextCursor
    },
    setHistoryCallsAppend(state, action: PayloadAction<ICall>) {
      state.data.callHistory.data = [
        action.payload,
        ...state.data.callHistory.data,
      ]
    },
    setHistoryCallsUpdate(state, action: PayloadAction<ICall>) {
      state.data.callHistory.data = state.data.callHistory.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload
        }

        return item
      })
    },
    setHistoryCallsUpdateObject(
      state,
      action: PayloadAction<NonNullable<ICall['subscriber']['storageObject']>>
    ) {
      state.data.callHistory.data = state.data.callHistory.data.map((item) => {
        if (item.subscriber.storageObject?.uniqId === action.payload.uniqId) {
          return {
            ...item,
            subscriber: {
              ...item.subscriber,
              storageObject: action.payload,
            },
          }
        }

        return item
      })
    },
    setWidgetState(
      state,
      action: PayloadAction<moduleState.IState['widgetOptions']>
    ) {
      state.widgetOptions = action.payload
    },

    setMicroState(state, action: PayloadAction<boolean>) {
      state.widgetOptions.microMuted = action.payload
    },

    setUserCall(state, action: PayloadAction<IToWhomCall>) {
      state.data.user = { ...state.data.user, ...action.payload }
    },
    resetUserState(state) {
      state.data.user = { number: '' }
    },

    setHistoryCalls(state, action: PayloadAction<IResponse<ICall[]>>) {
      state.data.callHistory.data = action.payload.data
      state.data.callHistory.cursor = action.payload.meta.nextCursor
    },
    setActiveCalls(state, action: PayloadAction<ICall[]>) {
      action.payload.forEach((call, index) => {
        const isActiveStatus = ['initiated', 'in-progress'].includes(
          call.status
        )
        const tenMinutesAgo = subMinutes(new Date(), 10)
        const targetDate = new Date(call.createdAt)
        const isActiveTime = isAfter(targetDate, tenMinutesAgo)
        const isActive = isActiveTime && isActiveStatus
        if (isActive) {
          const isAlready = state.data.activeCalls?.find(
            (activeCall) => activeCall.id === call.id
          )

          if (isAlready) {
            state.data.activeCalls = state.data.activeCalls?.map(
              (activeCall) => {
                if (activeCall.id === call.id) {
                  return {
                    ...activeCall,
                    ...call,
                  }
                }

                return activeCall
              }
            )
          } else {
            state.data.activeCalls = [call, ...state.data.activeCalls]
          }
        } else {
          state.data.activeCalls = state.data.activeCalls?.filter(
            (activeCall) => activeCall.id !== call.id
          )
        }
      })
    },
    setActiveCallsAutoClear(state) {
      state.data.activeCalls = state.data.activeCalls.filter((call) => {
        const isActiveStatus = ['initiated', 'in-progress'].includes(
          call.status
        )
        const tenMinutesAgo = subMinutes(new Date(), 10)
        const targetDate = new Date(call.updatedAt)
        const isActiveTime = isAfter(targetDate, tenMinutesAgo)
        const isClosed = call.isClosed === true
        const isActive = isActiveTime && isActiveStatus && !isClosed

        return isActive
      })
    },
    setActiveCallsClose(state, action: PayloadAction<{ id: number }>) {
      state.data.activeCalls = state.data.activeCalls.filter(
        (call) => call.id !== action.payload.id
      )
    },
  },
  extraReducers: (builder) => {
    builderSagaCommunications<moduleState.IState>(builder, moduleActions)
  },
})

export const setters = slice.actions
export const actions = moduleActions
export const reducers = { [moduleState.PREFIX]: slice.reducer }
export const sagas = moduleSagas
export const state = moduleState
export const selectors = moduleSelectors

export const injectStore = () => {
  store.injectReducer?.(moduleState.PREFIX, slice.reducer)
  store.injectSaga?.(moduleState.PREFIX, sagas)
}
